<template>
  <q-page padding class="window-height window-width row justify-center items-center">
    <div class="column full-width">
      <div class="row">

      </div>
      <div class="row">
        <div v-if="sessionChecked" class="col-xs-12 col-sm-12 col-md-4 offset-md-4">
          <q-card dark :bordered="!isMobile" :flat="isMobile"
                  :class="{ 'shadow-4': !isMobile, 'q-pa-lg': !isMobile, 'q-pa-sm': isMobile }">
            <h5 class="text-h5 text-white q-my-md text-center">Sign In</h5>
            <p class="text-bold text-body1 text-center text-negative">{{ error }}</p>
            <q-form
              @submit="onSubmit"
              @reset="onReset"
              class="q-gutter-md q-mt-md"
            >
              <q-input
                ref="emailRef"
                dark
                filled
                standout
                color="secondary"
                v-model="email"
                type="email"
                label="Email Address"
                hint="Enter your email"
                lazy-rules
                autofocus
                :rules="fieldEmailRules"
              />

              <q-input
                ref="passwordRef"
                dark
                filled
                standout
                color="secondary"
                type="password"
                v-model="password"
                label="Password"
                hint="Enter your password"
                lazy-rules
                :rules="fieldPasswordRules"
                autocomplete
              />

              <div class="q-mt-md">
                <q-btn label="Login" type="submit" color="secondary" class="full-width" :loading="loading" :disable="loading || disableAction" size="lg"/>
              </div>
              <div v-if="error && email" class="text-center">
                <q-btn flat no-caps color="accent" class="text-caption" label="Having trouble, sign in with magic link." :disable="loading || disableAction" @click="onMagicLinkSignIn"/>
              </div>
            </q-form>
            <q-card-actions align="right">
              <a class="q-my-md text-secondary text-bold cursor-pointer" @click="onForgotPassword">Forgot Password?</a>
            </q-card-actions>
          </q-card>
          <h5 class="text-body2 text-white q-my-md text-center">Don't have an account? <span class="text-secondary text-bold cursor-pointer" @click="onSignup">Sign up here</span></h5>
        </div>
        <div v-else class="col-xs-12 col-sm-12 col-md-4 offset-md-4">
          <q-card flat class="relative-position" style="background-color:transparent;height:50px">
            <q-inner-loading dark showing style="background-color:transparent;">
              <q-spinner-dots size="50px" color="secondary" />
            </q-inner-loading>
          </q-card>
        </div>
      </div>
      <div class="row">
        <div class="col">

        </div>
      </div>
    </div>
  </q-page>
</template>
<script>
import { ref, onMounted } from "vue"
import { useRouter, useRoute } from "vue-router"
import { loggerFactory } from '../../logger'
import { useUserStore } from '../../stores/user-store'
import { useQuasar } from "quasar"
import { track } from '../../rum'

const logger = loggerFactory("LoginPage")
export default {
  name: "LoginPage",
  setup() {
    const $q = useQuasar()
    const isMobile = $q.platform.is.mobile
    const router = useRouter()
    const route = useRoute()
    const store = useUserStore()
    const error = ref(null)
    const email = ref(null)
    const emailRef = ref(null)
    const password = ref(null)
    const passwordRef = ref(null)
    const loading = ref(false)
    const disableAction = ref(false)
    const sessionChecked = ref(false)

    const fieldEmailRules = ref([
      val => !!val || "Email is required"
    ])
    const fieldPasswordRules = ref([
      val => !!val || "Password is required"
    ])

    const notifyError = (message) => {
      $q.notify({ type: "negative", message: message, timeout: 2500 })
    }

    const sessionSuccess = () => {
      disableAction.value = true
      $q.notify({
        message: 'Initializing your session',
        color: 'positive',
        timeout: 1000
      })
      setTimeout(() => {
        const nextUrl = route.query?.nextUrl
          ? decodeURIComponent(route.query.nextUrl)
          : "/start"
        router.replace(nextUrl)
      }, 2000)
    }

    const magicLinkSuccess = () => {
      disableAction.value = true
      $q.notify({
        message: 'Check your email inbox for a magic link to login',
        color: 'positive',
        timeout: 5000
      })
      setTimeout(() => {
        disableAction.value = false
      }, 5000)
    }

    const onSubmit = () => {
      emailRef.value.validate()
      passwordRef.value.validate()

      if(emailRef.value.hasError || passwordRef.value.hasError) {
        notifyError("Please complete all fields before continuing.")
        return
      }

      const finalEmail = email.value.trim()
      const finalPassword = password.value.trim()

      logger.debug("Login user", finalEmail)
      loading.value = true
      track("login", { email: finalEmail })
      store.signIn(finalEmail, finalPassword)
        .then((user) => {
          logger.debug(`Signed in new user`, user)
          if(user) {
            track("login_success", { email: finalEmail })
            sessionSuccess()
          } else {
            track("login_failure", { email: finalEmail })
            notifyError("Unable to process your login attempt at this time.")
          }
        })
        .catch((err) => {
          error.value = err.message
          track("login_failure", { email: finalEmail, message: err.message })
          notifyError(err.message)
        })
        .finally(() => loading.value = false)
    }

    const onMagicLinkSignIn = () => {
      emailRef.value.validate()

      if(emailRef.value.hasError) {
        notifyError("Please complete email fields before continuing.")
        return
      }

      const finalEmail = email.value.trim()
      logger.debug("MagicLink Login user", finalEmail)
      loading.value = true
      track("login_magiclink", { email: finalEmail })
      store.signInWithEmail(finalEmail)
        .then((user) => {
          track("login_magiclink_success", { email: finalEmail })
          magicLinkSuccess()
        })
        .catch((err) => {
          error.value = err.message
          track("login_magiclink_failure", { email: finalEmail, message: err.message })
          notifyError(err.message)
        })
        .finally(() => loading.value = false)
    }

    const onReset = () => {
      email.value = null
      password.value = null
      emailRef.value.resetValidation()
      passwordRef.value.resetValidation()
    }

    const onForgotPassword = () => {
      logger.debug("Forgot password...")
      router.replace({ path: '/auth/forgot' })
    }

    const onSignup = () => {
      router.replace({ path: '/auth/register' })
    }

    const tryCurrentUser = () => {
      store.currentUser()
        .then((user) => {
          if(user) {
            logger.debug("Loaded user, skip login...", user)
            sessionSuccess()
          } else {
            sessionChecked.value = true
          }
        })
        .catch((err) => sessionChecked.value = true)
    }

    onMounted(() => {
      switch(route.query?.msg) {
        case "timeout":
          sessionChecked.value = true
          error.value = "Your session has timed out. Please login again."
          break;
        case "signout":
          sessionChecked.value = true
          error.value = "You have been signed out. Please login again."
          break;
        default:
          tryCurrentUser()
          break;
      }
    })

    return {
      isMobile,
      error,
      email,
      emailRef,
      password,
      passwordRef,
      loading,
      disableAction,
      sessionChecked,
      fieldEmailRules,
      fieldPasswordRules,
      onSubmit,
      onMagicLinkSignIn,
      onReset,
      onForgotPassword,
      onSignup
    }
  }
}
</script>

<style scoped>

</style>
